import * as React from 'react';

import { Button, Checkbox, Flex, Input, List, Select } from 'antd';

import {
  CloseOutlinedIcon,
  MenuOutlinedIcon,
  PlusOutlinedIcon,
} from 'components';
import {
  DND_CSS,
  dndArrayMove,
  DndSortableContext,
  useDndSortable,
} from 'context';
import type { NoticeDto, NoticeType } from 'types';
import { cn } from 'utils';

const NOTICE_TYPE_OPTIONS: {
  value: NoticeType;
  label: string;
  dotClassName: string;
}[] = [
  {
    value: 'Information',
    label: 'اطلاعات',
    dotClassName: 'bg-slate-300',
  },
  {
    value: 'Success',
    label: 'موفقیت',
    dotClassName: 'bg-[--ant-color-success]',
  },
  {
    value: 'Warning',
    label: 'هشدار',
    dotClassName: 'bg-[--ant-color-warning]',
  },
  { value: 'Error', label: 'خطا', dotClassName: 'bg-[--ant-color-error]' },
  { value: 'Primary', label: 'اصلی', dotClassName: 'bg-[--ant-color-primary]' },
];

const NOTICE_TYPE_SELECT_OPTIONS = NOTICE_TYPE_OPTIONS.map(
  ({ value, label, dotClassName }) => ({
    value,
    label: (
      <Flex align='center' gap={6}>
        <span
          className={cn('inline-block size-2 rounded-full', dotClassName)}
        />
        {label}
      </Flex>
    ),
  }),
);

const createEmptyNotice = (): NoticeDto => ({
  type: 'Information',
  isActive: false,
  content: '',
});

type NoticeListItemProps = {
  index: number;
  notice: NoticeDto;
  onChange: (index: number, patch: Partial<NoticeDto>) => void;
  onRemove: (index: number) => void;
};

const NoticeListItem = ({
  index,
  notice,
  onChange,
  onRemove,
}: NoticeListItemProps) => {
  const id = String(index);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useDndSortable({ id });

  return (
    <List.Item
      ref={setNodeRef}
      className='gap-2 rounded border bg-slate-50 px-3'
      style={{
        transform: DND_CSS.Transform.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
      }}
      actions={[
        <Button
          key='remove'
          danger
          type='text'
          size='small'
          icon={<CloseOutlinedIcon />}
          onClick={() => onRemove(index)}
        />,
      ]}
    >
      <Flex align='center' gap='small' className='min-w-0 flex-1'>
        <MenuOutlinedIcon
          className='shrink-0 cursor-grab text-[--ant-color-text-tertiary] active:cursor-grabbing'
          {...attributes}
          {...listeners}
        />
        <Select<NoticeType>
          className='w-36 shrink-0'
          value={notice.type}
          options={NOTICE_TYPE_SELECT_OPTIONS}
          onChange={type => onChange(index, { type })}
        />
        <Checkbox
          className='shrink-0'
          checked={notice.isActive}
          onChange={e => onChange(index, { isActive: e.target.checked })}
        >
          فعال
        </Checkbox>
        <Input
          className='min-w-0 flex-1'
          placeholder='متن اعلان را وارد کنید'
          value={notice.content}
          onChange={e => onChange(index, { content: e.target.value })}
        />
      </Flex>
    </List.Item>
  );
};

type NoticesSortableListProps = {
  value?: NoticeDto[];
  onChange?: (value: NoticeDto[]) => void;
  className?: string;
};

const NoticesSortableList = ({
  value = [],
  onChange,
  className,
}: NoticesSortableListProps) => {
  const handleItemChange = (index: number, patch: Partial<NoticeDto>) => {
    onChange?.(
      value.map((notice, i) =>
        i === index ? { ...notice, ...patch } : notice,
      ),
    );
  };

  const handleRemove = (index: number) => {
    onChange?.(value.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    onChange?.([...value, createEmptyNotice()]);
  };

  return (
    <div className={cn(className, 'flex flex-col gap-2')}>
      {!!value.length && (
        <DndSortableContext
          dnd={{
            onDragEnd: ({ active, over }) => {
              const overId = over?.id;
              if (!overId || active.id === overId) return;

              const activeIndex = Number(active.id);
              const overIndex = Number(overId);
              if (Number.isNaN(activeIndex) || Number.isNaN(overIndex)) return;

              onChange?.(dndArrayMove(value, activeIndex, overIndex));
            },
          }}
          sortable={{
            items: value.map((_, index) => String(index)),
            children: (
              <List
                size='small'
                className='mt-2 [&_.ant-list-items]:flex [&_.ant-list-items]:flex-col [&_.ant-list-items]:gap-1.5'
                dataSource={value}
                renderItem={(notice, index) => (
                  <NoticeListItem
                    key={index}
                    index={index}
                    notice={notice}
                    onChange={handleItemChange}
                    onRemove={handleRemove}
                  />
                )}
              />
            ),
          }}
        />
      )}

      <Button
        type='dashed'
        block
        icon={<PlusOutlinedIcon />}
        onClick={handleAdd}
      >
        افزودن اعلان
      </Button>
    </div>
  );
};

export { NoticesSortableList };
